/* eslint-disable no-console */
import {ErrorEvent as SentryEvent, EventHint, addBreadcrumb, captureException} from '@sentry/browser';
import {Action, AnyAction, Dispatch, Middleware} from 'redux';
import {debounce} from 'ts-debounce';

import * as analytics from 'analytics/Analytics';
import * as BenchlingUtil from 'BenchlingUtil';
import {isHandledApolloError} from 'graphql-client/createErrorLink';
import notifications from 'notifications';
import {GENERIC_ERROR_MESSAGE} from 'ui-library/ErrorConstants';
import attemptCaptureBreadcrumb from 'util/attemptCaptureBreadcrumb';
import * as keyboard from 'util/Keyboard';
import * as logging from 'util/Logging';
import * as net from 'util/Net';

// For cross-browser compatibility, we can't assume that an error's `message` and `source` are defined!
type BrowserCompatErrorMessage = string | null | undefined;
type BrowserCompatErrorSource = string | null | undefined;
type BrowserCompatErrorHandler = (
  message: BrowserCompatErrorMessage,
  source: BrowserCompatErrorSource,
  ...otherArgs: Array<unknown>
) => void;

// In some cases (e.g., the Honey chrome extension), if the promise is rejected with a
// string, it'll be surfaced as event.detail.reason.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type BrowserCompatPromiseRejectionEvent = PromiseRejectionEvent & {detail?: {reason?: any}};
type BrowserCompatUnhandledRejectionHandler = (
  event: BrowserCompatPromiseRejectionEvent
) => string | null | undefined;

let isUnloading = false;

const ADBLOCK_ERRORS = [
  // Some AdBlockers in specific browsers will error with really unhelpful messages when Segment tries to
  // connect for various analytics methods.
  /^InvalidAccessError$/, // Safari uBlock
  /^: $/, // Firefox AdBlockPlus -- frustratingly, ": " is the full message we get
];

const USER_FACING_WHITE_LISTED_URLS = [window.location.hostname, /bnchcdn\.com/];
if (window.debug) {
  USER_FACING_WHITE_LISTED_URLS.push(/webpack/);
}

const IGNORED_ERRORS = [
  /.*Blocked a frame with origin "https:\/\/benchling.com" from accessing a cross-origin frame\.$/,
  /.*Blocked a frame with origin "https:\/\/benchling.com" from accessing a frame with origin/,
  /Script error\./,
  /tagpro is not defined/,
  // FF-only, noisy error from client machine running out of memory. While this may be because of Benchling,
  // it's also likely due to other tabs being open and is hard to use as a signal.
  /out of memory/,
  // Chrome iOS issue with forms, see T8083
  /__gCrWeb.autofill.extractForms/,
  // Firefox 54 has a large number of these errors happening, mostly in React land. Most of these seem to
  // not affect the user. https://github.com/facebook/react/issues/10181
  /Permission denied to access property "target"/,
  // Similar error to above, but in jQuery. One repro (that I haven't actually been able to trigger) has to
  // do with dragging the selection bars in the sequence map in firefox.
  /Permission denied to access property "type"/,

  // Honey (chrome extension) rejects a promise with this string
  /Not a Product/,

  /Cannot read property '_avast_submit' of undefined/,

  // Caused by AdRoll and doesn't cause any user-facing issues
  /Uncaught TypeError: Cannot read property 'record_adroll_email' of undefined/,

  // Caused by Mixmax and doesn't cause any user-facing issues
  /Cannot assign to read only property 'open' of object '#<XMLHttpRequest>'/,

  // Unhandled promise from a browser extension? We don't ever reject a native promise with this message.
  /^timeout$/,

  // Adblock-related errors
  /_isMatchingDomain is not defined/,
  /^InvalidAccessError$/,

  // StackOverflow suggests this issue is not problematic - "This error means that ResizeObserver was not able to
  // deliver all observations within a single animation frame. It is benign (your site will not break)"
  // - Comment on https://stackoverflow.com/q/49384120/319912
  /ResizeObserver loop limit exceeded/,

  // Similarly, it seems that if what initializes the ResizeObserver skips events, it may trigger the
  // following error. See https://www.w3.org/TR/resize-observer/#html-event-loop, "If Document has skipped
  // observations..."
  /ResizeObserver loop completed with undelivered notifications/,

  // Doesn't provide a stack trace, and causes a fair amount of noise. Ignoring for now.
  // See https://benchling.slack.com/archives/C2FB38R1D/p1597209685236400 for more details
  /^{}$/,

  // Safari-only issue with no stack trace. Likely caused by an extension, so ignoring for now.
  // See https://benchling.slack.com/archives/C2FB38R1D/p1602004628035600
  /^Can't find variable: ts$/,

  // Both of these issues are only happening on Firefox. Likely caused by an extension, so ignoring for now.
  /^TypeError: can't redefine non-configurable property "availWidth"$/,
  /^TypeError: can't redefine non-configurable property "userAgent"$/,
];

function isAdblockError(message: BrowserCompatErrorMessage): boolean {
  for (let i = 0; i < ADBLOCK_ERRORS.length; i++) {
    const error = ADBLOCK_ERRORS[i];
    if (typeof message === 'string' && message.match(error)) {
      return true;
    }
  }
  return false;
}

function _isWhiteListedURL(source: BrowserCompatErrorSource): boolean {
  if (source == null) {
    return false;
  }
  return USER_FACING_WHITE_LISTED_URLS.some((url) => {
    if (typeof url === 'string') {
      return source.toUpperCase().includes(url.toUpperCase());
    } else {
      return new RegExp(url, 'i').test(source);
    }
  });
}

/**
 * Check if we should skip any user-facing reporting of this error. Source may be null, e.g. when
 * handling promise rejections.
 */
function isIgnoredError(message: BrowserCompatErrorMessage, source: BrowserCompatErrorSource): boolean {
  if (isUnloading && BenchlingUtil.firefox) {
    // See rationale in `handleBeforeUnload` comment
    return true;
  }
  if (isAdblockError(message)) {
    return true;
  }
  if (source && !_isWhiteListedURL(source)) {
    return true;
  }

  for (let i = 0; i < IGNORED_ERRORS.length; i++) {
    const error = IGNORED_ERRORS[i];
    if (typeof message === 'string' && message.match(error)) {
      return true;
    }
  }

  return false;
}

let _lastErrorSentTime: number | null = null;

function processErrorLastMinute(data: SentryEvent): void {
  const now = new Date().getTime();
  const errorLastMinute = _lastErrorSentTime != null && now - _lastErrorSentTime < 60 * 1000;
  const {tags = {}} = data;
  tags.error_last_minute = errorLastMinute;
  data.tags = tags;
  _lastErrorSentTime = now;
}

function _getFSSessionURL(): string | null {
  if (!window.FS) {
    return null;
  }
  // We set window.FS when loading in FS, but sometimes (slow network, blocked by extension) the FS JS
  // never loads. In that case, we shouldn't set a session URL even though window.FS is set.
  if (window.FS.getCurrentSessionURL) {
    return window.FS.getCurrentSessionURL(true);
  }
  return '[NOT_LOADED]';
}

function dataCallback(
  event: SentryEvent,
  hint: EventHint
): SentryEvent | PromiseLike<SentryEvent | null> | null {
  if (!shouldSendCallback(event, hint)) {
    return null;
  }

  try {
    processErrorLastMinute(event);

    const fsSessionURL = _getFSSessionURL();
    if (fsSessionURL) {
      const {extra = {}} = event;
      extra.FSSessionURL = fsSessionURL;
      event.extra = extra;
    }
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    // We should avoid erroring inside an error handler.
    console.error(`An error occurred while processing Sentry data: ${e.message}`);
  }
  return event;
}

const ERROR_THROTTLE_TIME_BUCKET_MINUTES = 30;
const ERROR_THROTTLE_TIME_BUCKET_MS = ERROR_THROTTLE_TIME_BUCKET_MINUTES * 60 * 1000;
const ERROR_THROTTLE_TIME_BUCKET_MAX_SIZE = 10;
const _errorTimesMillis: Array<number> = [];

function shouldSendCallback(event?: SentryEvent, hint?: EventHint): boolean {
  if (isUnloading) {
    return false;
  }
  const currentMillis = new Date().getTime();
  _errorTimesMillis.push(currentMillis);
  while (currentMillis - _errorTimesMillis[0] > ERROR_THROTTLE_TIME_BUCKET_MS) {
    _errorTimesMillis.shift();
  }
  return _errorTimesMillis.length <= ERROR_THROTTLE_TIME_BUCKET_MAX_SIZE;
}

// Send the full story session url as a request header so that it's accessible in Python
// This allows us to link to the full story session on the Sentry event page of a server-side error
function fsURLSetter(this: JQueryAjaxSettings, xhr: XMLHttpRequest, settings: JQueryAjaxSettings): void {
  const fsSessionURL = _getFSSessionURL();
  if (this.crossDomain || !fsSessionURL) {
    return;
  }
  try {
    xhr.setRequestHeader('X-FSSessionUrl', fsSessionURL);
    // eslint-disable-next-line benchlint/always-throw-from-catch
  } catch (e) {
    // do nothing;
  }
}

/**
 * Record keydown breadcrumbs in Sentry if modifier or non-typing keys are downed.
 */
function handleKeydownForSentry(event: KeyboardEvent): void {
  if (keyboard.isNonTypingKey(event)) {
    attemptCaptureBreadcrumb({
      category: 'ui.keydown',
      message: keyboard.getShortcutText(event),
      level: 'info',
    });
  }
}

const debouncedResetIsUnloading = debounce(() => {
  isUnloading = false;
}, /* wait */ 5000);

function handleBeforeUnload(event: Event): void {
  // In Firefox, we've observed that when a user navigates away from the page, pending jqXHRs will sometimes
  // resolve with `undefined`, causing the majority of `done` handlers to unexpectedly crash. In many cases,
  // the browser is still able to make a new XHR to Sentry with this downstream failure. Since there is
  // no practical user impact, we simply disable logging to Sentry at this point (via shouldSendCallback).
  isUnloading = true;
  // Unfortunately it seems that jqXHRs are resolved *before* the unload handler, which is when we would
  // definitively know that the user is navigating away from the page. We therefore use beforeunload as a
  // proxy but only silence the errors for 5 seconds in case the user cancels navigation away.
  debouncedResetIsUnloading();

  // To help with debugging a Sentry event in case the above measure is not sufficient.
  addBreadcrumb({
    level: 'info',
    category: 'browser.beforeunload',
    message: 'User is navigating off the page',
  });
}

function addListeners(): void {
  window.addEventListener('keydown', handleKeydownForSentry, /* useCapture */ true);
  window.addEventListener('beforeunload', handleBeforeUnload);
}

function removeListeners(): void {
  window.removeEventListener('keydown', handleKeydownForSentry, /* useCapture */ true);
  window.removeEventListener('beforeunload', handleBeforeUnload);
}

// Middleware which creates Sentry breadcrumbs from redux actions.
export const captureActionsInSentryReduxMiddleware: Middleware =
  () =>
  (next: Dispatch<AnyAction>): Dispatch<AnyAction> =>
  <A extends Action>(action: A): A => {
    attemptCaptureBreadcrumb({
      message: action.type,
      category: 'redux-action',
      level: 'info',
    });
    return next(action);
  };

const APOLLO_MISSING_FIELD_REGEX = /Missing field ([a-zA-Z]+) in {/;
const TYPENAME_REGEX = /"__typename": "([a-zA-Z]+)"/;

// Console warning override to capture deep Apollo error
const oldConsoleWarn = console.warn.bind(console);

// eslint-disable-next-line @typescript-eslint/no-explicit-any
console.warn = (message?: any, ...optionalParams: Array<any>) => {
  if (message != null && typeof message === 'string' && message.match(APOLLO_MISSING_FIELD_REGEX)) {
    const field = message.match(APOLLO_MISSING_FIELD_REGEX)![1];

    // We normally expect to find type, but this can be missing in some of our contrived test examples
    const type = message.match(TYPENAME_REGEX) && message.match(TYPENAME_REGEX)![1];
    if (!window.debug && !type) {
      logging.warn(`Expected to find typename in Apollo missing field warning for ${field}`);
    }

    const missingFieldMessage = type ? `Missing field ${field} on ${type}` : message;
    captureException(missingFieldMessage, {
      level: 'warning',
      extra: {field, type, originalWarning: message},
    });

    notifications.error(
      window.debug
        ? `Apollo: unexpected missing field ${field} on ${type}. Original message: ${message}...`
        : GENERIC_ERROR_MESSAGE
    );
  }

  oldConsoleWarn(message, ...optionalParams);
};

/**
 * Reports problems and returns true if we should skip any further handling of this error,
 * false otherwise.
 *
 * source may be null, e.g. when handling promise rejections.
 */
function handleError(message: BrowserCompatErrorMessage, source: BrowserCompatErrorSource): boolean {
  if (isAdblockError(message)) {
    // Silently swallow this. This is likely from one of our analytics methods. If we try to send an
    // analytics event in this error handler, we'll just crash. Unfortunately, if we call oldOnError
    // with this, we'll end up with an error in Sentry even though the user doesn't see anything.
    return true;
  }

  // Show standard error message if not caught.
  if (!isIgnoredError(message, source)) {
    notifications.error(
      `\
Sorry, you ran into an error. Try refreshing the page, or reach out to support@benchling.com \
if the issue persists.`,
      {id: '_globalOnError'}
    );
    if (window.debug) {
      notifications.info(`[DEBUG] ${message}`);
    }
    analytics.track('system', 'trigger_error', {
      message,
      error_origin: 'js',
      beforeUnloadType: window.beforeUnloadType,
    });
  }
  return false;
}

function installErrorHandler(): void {
  const oldOnError: BrowserCompatErrorHandler = window.onerror;

  window.onerror = function errorHandler(
    message: BrowserCompatErrorMessage,
    source: BrowserCompatErrorSource,
    ...otherArgs: Array<unknown>
  ): void {
    const shouldSkip = handleError(message, source);
    if (shouldSkip) {
      return;
    }
    if (oldOnError) {
      oldOnError(message, source, ...otherArgs);
    }
    // Force the assignment here because TS's onerror definition is incorrect. We augment it in Types.ts,
    // but that's not enough to make this assignment work fully.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any;
}

function installUnhandledRejectionHandler(): () => void {
  const oldOnUnhandledRejection: BrowserCompatUnhandledRejectionHandler | null = window.onunhandledrejection;

  window.onunhandledrejection = function unhandledRejectionHandler(
    event: BrowserCompatPromiseRejectionEvent
  ): string | null | undefined {
    const exception = event.reason;
    // For the moment, all 400/500 HTTP responses are shown as an error to the user (sometimes
    // with a friendly message) and are not additionally reported to Sentry.
    if (net.isServerRequestError(exception)) {
      notifications.notifyError(exception, exception.notificationOptions);
      return undefined;
    }
    // Avoid double-handling ApolloErrors.This code path is exercised by non-React usages of ApolloClient,
    // e.g. when using client/graphql-client/ApolloWrappers.ts
    if (isHandledApolloError(event)) {
      return undefined;
    }

    const errorMessage = event.reason?.message;
    const shouldSkip = !errorMessage || handleError(errorMessage, null);
    if (shouldSkip) {
      return undefined;
    }
    captureException(exception);
    // Firefox and Safari don't print the error to the console, so print it explicitly. We assume
    // that browsers implementing native promise rejection handling also print a message to the
    // console by default.
    if (typeof PromiseRejectionEvent !== 'function') {
      console.error(exception);
    }
    return oldOnUnhandledRejection && oldOnUnhandledRejection(event);
  };
  return () => {
    window.onunhandledrejection = oldOnUnhandledRejection;
  };
}

export {
  addListeners,
  removeListeners,
  dataCallback,
  IGNORED_ERRORS,
  installErrorHandler,
  installUnhandledRejectionHandler,
  isAdblockError,
  isIgnoredError,
  shouldSendCallback,
  USER_FACING_WHITE_LISTED_URLS,
  fsURLSetter,
};
